import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Download, Package } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { catalogEntries } from './catalogEntries'

type Code = 'en' | 'ru' | 'uz'
const LANGS: { code: Code; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'uz', name: "O'zbek" },
]

/**
 * A22.02 — Til paketlari. Har til (en/ru/uz) uchun bitta paket: kalit soni,
 * qamrov (en'ga nisbatan tarjima ulushi), va paketni JSON qilib yuklab olish.
 * Manba = klient i18n katalogi. Backend YO'Q.
 */
export function LocalizationPacks() {
  const t = useT()
  const rows = useMemo(() => catalogEntries().filter((e) => !e.fn), [])

  const packs = LANGS.map((l) => {
    const total = rows.length
    const translated = l.code === 'en' ? total : rows.filter((e) => e[l.code] !== e.en && e[l.code] !== '').length
    return { ...l, total, translated, pct: total ? Math.round((translated / total) * 100) : 100 }
  })

  const download = (code: Code) => {
    const obj: Record<string, string> = {}
    for (const e of rows) obj[e.key] = e[code]
    const url = URL.createObjectURL(new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `grom-${code}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <PageHeader
        title={t.loc.packsTitle}
        screenId="A22.02"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
        {packs.map((p) => (
          <div key={p.code} className="rounded-lg border border-border-default bg-surface-1 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Package size={16} className="text-accent" />
              <span className="font-medium text-primary">{p.name}</span>
              <span className="font-mono text-xs text-tertiary">{p.code}</span>
            </div>
            <div className="mb-1 flex justify-between text-sm">
              <span className="text-secondary">{t.loc.keys}</span>
              <span className="font-mono text-primary">{p.total.toLocaleString()}</span>
            </div>
            <div className="mb-1 flex justify-between text-sm">
              <span className="text-secondary">{t.loc.coverage}</span>
              <span className="font-mono text-primary">{p.pct}%</span>
            </div>
            <div className="mb-3 mt-1 h-1.5 overflow-hidden rounded-full bg-surface-3">
              <div className="h-full rounded-full bg-accent" style={{ width: `${p.pct}%` }} />
            </div>
            <button
              onClick={() => download(p.code)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary hover:bg-surface-3"
            >
              <Download size={14} /> JSON
            </button>
          </div>
        ))}
      </div>
    </>
  )
}
